import {
  Appointment,
  AppointmentReq,
  AvailableTimeSlotsReq,
  AvailableTimeSlotsResp,
  SessionProgress,
  SimpleAppointment,
} from "../interfaces/services/Apoointment";
import apiUrls from "../store/apiUrls";
import apiService, { apiCachedGet, invalidateCachePrefix } from "./apiService";

const APPT_NS = "APPT:";
const CK = {
  listBySalon: (salonCode: string) => `${APPT_NS}list:salon:${salonCode}`,
  listBySalonFiltered: (salonCode: string, query: string) =>
    `${APPT_NS}list:salon:${salonCode}?${query}`,
  simpleBySalon: (salonCode: string) => `${APPT_NS}simple:salon:${salonCode}`,
  countFiltered: (query: string) => `${APPT_NS}count?${query}`,
  item: (code: string) => `${APPT_NS}item:${code}`,
  customersBySalon: (query: string) => `${APPT_NS}customers?${query}`,
  byCustomer: (customerCode: string) => `${APPT_NS}byCustomer:${customerCode}`,
  listMy: () => `${APPT_NS}my`,
  availableSlots: (query: string) => `${APPT_NS}available:${query}`,
};

function toAvailableQuery(q: AvailableTimeSlotsReq) {
  const p = new URLSearchParams();
  if (q.date) p.append("Date", q.date);
  if (q.employeeCode) p.append("EmployeeCode", q.employeeCode);
  if (q.day) p.append("OperatingHours.Day", q.day);
  if (q.openTime) p.append("OperatingHours.OpenTime", q.openTime);
  if (q.closeTime) p.append("OperatingHours.CloseTime", q.closeTime);
  if (typeof q.serviceDuration === "number")
    p.append("ServiceDuration", String(q.serviceDuration));
  return p.toString(); // cache key ve URL için deterministik
}

const appointmentService = {
  getSimpleBySalon: (salonCode: string): Promise<SimpleAppointment[]> => {
    const url = `${apiUrls.appointment.appointment.getSimpleBySalon}/${salonCode}`;
    return apiCachedGet<SimpleAppointment[]>("APPOINTMENT", url, {
      ttl: 10 * 60 * 1000,
      store: "memory",
      keyExtra: CK.simpleBySalon(salonCode),
      swr: false,
    });
  },
  createAppointment: (appointment: AppointmentReq): Promise<string> => {
    invalidateCachePrefix(APPT_NS);
    return apiService.APPOINTMENT.post(
      `${apiUrls.appointment.appointment.createAppointment}`,
      appointment
    );
  },
  getAppointmentsByCustomerCode: (
    customerCode: string
  ): Promise<Appointment[]> => {
    const url = `${apiUrls.appointment.appointment.appointmentsByCustomer}/${customerCode}`;
    return apiCachedGet<Appointment[]>("APPOINTMENT", url, {
      ttl: 10 * 60 * 1000,
      store: "memory",
      keyExtra: CK.byCustomer(customerCode),
      swr: false,
    });
  },
  getMyAppointments: (): Promise<Appointment[]> => {
    const url = `${apiUrls.appointment.appointment.myAppointments}`;
    return apiCachedGet<Appointment[]>("APPOINTMENT", url, {
      ttl: 10 * 60 * 1000,
      store: "memory",
      keyExtra: CK.listMy(),
      swr: false,
    });
  },
  getCurrentSession: (
    salonCode: string,
    customerCode: string,
    serviceCode: string
  ): Promise<SessionProgress> => {
    const p = new URLSearchParams();
    p.append("salonCode", salonCode);
    p.append("customerCode", customerCode);
    p.append("serviceCode", serviceCode);
    const url = `${
      apiUrls.appointment.appointment.sessionProgress
    }?${p.toString()}`;
    return apiCachedGet<SessionProgress>("APPOINTMENT", url, {
      ttl: 10 * 60 * 1000,
      store: "memory",
      keyExtra: CK.listMy(),
      swr: false,
    });
  },
  getAvailableTimeSlots: (
    params: AvailableTimeSlotsReq
  ): Promise<AvailableTimeSlotsResp> => {
    const query = toAvailableQuery(params);
    const url = `${apiUrls.appointment.appointment.availableTimeSlots}${
      query ? `?${query}` : ""
    }`;
    return apiCachedGet<AvailableTimeSlotsResp>("APPOINTMENT", url, {
      ttl: 60 * 1000, // slotlar hızlı değişir: 1 dk makul
      store: "memory",
      keyExtra: CK.availableSlots(query), // 🔑 her kombinasyon ayrı cache
      swr: true, // varsa cache’i hemen döndür + arkaplanda tazele
    });
  },
};

export default appointmentService;
